import { z } from 'zod';

/**
 * Checkr background-check display statuses (T-116).
 * SMB mapping from Checkr's Integration Guidance webhook table — Eric combines
 * recruiting + adjudication, so we show Clear / Needs Review rather than
 * leaving consider results as Pending.
 */
export const BACKGROUND_CHECK_STATUSES = [
  'invitation_sent',
  'pending',
  'invitation_expired',
  'invitation_canceled',
  'clear',
  'clear_with_canceled',
  'needs_review',
  'canceled',
  'suspended',
  'disputed',
  'pre_adverse_action',
  'not_eligible',
] as const;

export const BackgroundCheckStatus = z.enum(BACKGROUND_CHECK_STATUSES);
export type BackgroundCheckStatus = z.infer<typeof BackgroundCheckStatus>;

/** Non-terminal — blocks a new order for the same resource. */
export const OPEN_BACKGROUND_CHECK_STATUSES: ReadonlySet<BackgroundCheckStatus> = new Set([
  'invitation_sent',
  'pending',
  'suspended',
  'disputed',
  'pre_adverse_action',
]);

export function isOpenBackgroundCheckStatus(status: BackgroundCheckStatus): boolean {
  return OPEN_BACKGROUND_CHECK_STATUSES.has(status);
}

/** Human labels for the resource detail UI. */
export const BACKGROUND_CHECK_STATUS_LABELS: Record<BackgroundCheckStatus, string> = {
  invitation_sent: 'Invitation sent',
  pending: 'Pending',
  invitation_expired: 'Invitation expired',
  invitation_canceled: 'Invitation canceled',
  clear: 'Clear',
  clear_with_canceled: 'Clear (canceled screenings)',
  needs_review: 'Needs review',
  canceled: 'Canceled',
  suspended: 'Suspended',
  disputed: 'Disputed',
  pre_adverse_action: 'Pre-adverse action',
  not_eligible: 'Not eligible',
};

export const BackgroundCheck = z.object({
  id: z.string().uuid(),
  resource_id: z.string().uuid(),
  checkr_candidate_id: z.string().nullable(),
  checkr_invitation_id: z.string().nullable(),
  checkr_report_id: z.string().nullable(),
  status: BackgroundCheckStatus,
  raw_result: z.string().nullable(),
  assessment: z.string().nullable(),
  includes_canceled: z.boolean(),
  invitation_url: z.string().nullable(),
  /** Constructed Checkr Dashboard deep-link when a report id is present. */
  report_dashboard_url: z.string().nullable(),
  ordered_at: z.string(),
  completed_at: z.string().nullable(),
});
export type BackgroundCheck = z.infer<typeof BackgroundCheck>;

export const BackgroundCheckListResponse = z.object({
  items: z.array(BackgroundCheck),
});
export type BackgroundCheckListResponse = z.infer<typeof BackgroundCheckListResponse>;

export const BackgroundCheckOrderResult = z.object({
  status: z.enum(['ordered', 'checkr_error', 'not_configured']),
  background_check: BackgroundCheck.nullable(),
  message: z.string().nullable(),
});
export type BackgroundCheckOrderResult = z.infer<typeof BackgroundCheckOrderResult>;
